import { NumericFormat } from 'react-number-format';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useMutation } from '@tanstack/react-query';

import { PyButton, TOASTER_TYPE, ToastMessage } from '../../../../../components';
import { convertFromNairaToKobo } from '../../../../../utils/helper';
import { usePayLinkCart } from '../../../context/PayLinkCartContext';
import { usePayLinkUser } from '../../../context/PaylinkUserContext';
import { checkoutWalletSession, getAllEnrolledLoyalty } from '../../../services/api';
import { ILoyaltyMember } from '../../../types/loyalty';
import { ICheckoutSessionPayload } from '../../../types/walllet';
import { formatPaylinkCart } from '../../../utils/helper';

interface IProps {
  amountToPay: number;
  rewardDiscount?: number;
  isDisabled?: boolean;
}

export const CheckoutButton = ({ amountToPay, rewardDiscount = 0, isDisabled = false }: IProps) => {
  const navigate = useNavigate();
  const {
    cardToken,
    user,
    deliveryNote,
    shippingRate,
    reward,
    freeShippingReward,
    pointMerchant,
    redeemedPoints,
    adminDetails,
  } = usePayLinkUser();
  const {
    sessionUniqueReferenceNumber,
    merchantSessionUrn,
    storeProfile,
    cartItems,
    currency,
    gatewaySetting,
  } = usePayLinkCart();
  // Shipping must be confirmed (with isAssistedShipping explicitly set) before checkout is allowed.
  const canCheckout = (amountToPay <= 0 || !!cardToken) && !!shippingRate && shippingRate.isAssistedShipping !== undefined;
  const onCheckoutWalletSession = useMutation({
    mutationKey: ["onCheckoutWalletSession"],
    mutationFn: checkoutWalletSession,
    onSuccess(res) {
      if (res.status) {
        navigate("/paylink/order-confirmed");
        localStorage.removeItem("paylink_cart_items");
        localStorage.removeItem("C_S");
        localStorage.removeItem("paylink_c_s");
        localStorage.removeItem(
          storeProfile?.customerUniqueReferenceNumber ?? "",
        );
      } else {
        ToastMessage(
          TOASTER_TYPE.ERROR,
          res.message || "An error occurred during checkout. Please try again.",
        );
      }
    },
    onError(error: any) {
      const apiError = error?.response?.data;
      const isRechargeError =
        // apiError?.code === "FAILED_CHECKOUT_WALLET_SESSION_REQUEST" ||
        apiError?.message?.includes("due to error recharging card payment") ||
        (typeof apiError?.message === "string" &&
          apiError.message.toLowerCase().includes("recharging card payment"));

      if (isRechargeError) {
        ToastMessage(
          TOASTER_TYPE.ERROR,
          "We were unable to charge your card. Please verify your card details, try updating your payment method, or use another card.",
        );
      } else {
        ToastMessage(
          TOASTER_TYPE.ERROR,
          apiError?.message || "An error occurred during checkout. Please try again.",
        );
      }
    },
  });

  const handleCheckout = async () => {
    if (
      !user ||
      !storeProfile ||
      !shippingRate ||
      // isAssistedShipping must be explicitly set (boolean) before the shipping
      // object is included in the request — never allow an undefined value through.
      shippingRate.isAssistedShipping === undefined ||
      !cardToken ||
      !amountToPay ||
      !gatewaySetting
    )
      return;
    let payload: ICheckoutSessionPayload = {
      deliveryNote,
      sessionUniqueReferenceNumber:
        sessionUniqueReferenceNumber || merchantSessionUrn,
      walletUniqueReferenceNumber: user.walletUniqueReferenceNumber,
      consumerUniqueReferenceNumber: user.consumerUniqueReferenceNumber,
      merchantUniqueReferenceNumber: storeProfile.customerUniqueReferenceNumber,
      // customerUniqueReferenceNumber: storeProfile.customerUniqueReferenceNumber,
      adminUniqueReferenceNumber: adminDetails?.uniqueReferenceNumber,
      isMarketplace: true,
      includesStockAllocation: true,
      cart: formatPaylinkCart(
        cartItems.map((item) => ({
          ...item,
          image: item.productImages[0].documentAbsoluteUri,
        })),
      ).map((item) => ({
        ...item,
        price: convertFromNairaToKobo(item.price),
      })),
      shipping: {
        ...shippingRate,
        charge: convertFromNairaToKobo(
          parseFloat(shippingRate.charge.toFixed(2)),
        ),
        // charge: freeShippingReward
        // ? convertFromNairaToKobo(Math.max(0, shippingRate.charge - Number(freeShippingReward.rewardEffectValueLimit/100 ?? 0)))
        // : convertFromNairaToKobo(parseFloat(shippingRate.charge.toFixed(2))),
      },
      recurrentBilling: {
        cardTokenUniqueReferenceNumber: cardToken.uniqueReferenceNumber,
        amount: convertFromNairaToKobo(parseFloat(amountToPay.toFixed(2))),
        authorizationCode: cardToken.authorizationCode as string,
        paymentUniqueReferenceNumber: uuidv4(),
        paymentItemUniqueReferenceNumber:
          sessionUniqueReferenceNumber || merchantSessionUrn,
        emailAddress: cardToken.emailAddress,
        customerId: cardToken.gatewayCustomerId,
        currency:
          gatewaySetting.paymentGatewayType === "STRIPE" ? "GBP" : "NGN",
        paymentMethod:
          gatewaySetting.paymentGatewayType === "STRIPE"
            ? cardToken.authorizationCode
            : "card",
      },
    };

    // if (reward) {
    //   const hasValidRewardRedemptionFields =
    //     !!reward.uniqueReferenceNumber &&
    //     !!reward.promotionUniqueReferenceNumber &&
    //     !!reward.customerUniqueReferenceNumber &&
    //     !!reward.loyaltyProgrammeTierUniqueReferenceNumber;

    //   // if (!hasValidRewardRedemptionFields) {
    //   //   ToastMessage(
    //   //     TOASTER_TYPE.ERROR,
    //   //     'Selected reward is no longer applicable. Please re-apply rewards.',
    //   //   );
    //   //   return;
    //   // }
    // }

    // if (freeShippingReward) {
    //   const hasValidShippingRewardFields =
    //     !!freeShippingReward.uniqueReferenceNumber &&
    //     !!freeShippingReward.promotionUniqueReferenceNumber &&
    //     !!freeShippingReward.customerUniqueReferenceNumber &&
    //     !!freeShippingReward.loyaltyProgrammeTierUniqueReferenceNumber;

    //   if (!hasValidShippingRewardFields) {
    //     ToastMessage(
    //       TOASTER_TYPE.ERROR,
    //       'Shipping reward is no longer applicable. Please re-apply rewards.',
    //     );
    //     return;
    //   }
    // }

    if (reward) {
      const rewardCustomerUrn = reward.customerUniqueReferenceNumber as string;
      const rewardPromotionUrn = reward.promotionUniqueReferenceNumber as string;
      const rewardTierUrn =
        reward.loyaltyProgrammeTierUniqueReferenceNumber as string;

      payload = {
        ...payload,
        rewardRedemption: {
          uniqueReferenceNumber: reward.uniqueReferenceNumber,
          discountAmountApplied: convertFromNairaToKobo(
            parseFloat(rewardDiscount.toFixed(2)),
          ),
          customerUniqueReferenceNumber: rewardCustomerUrn,
          promotionUniqueReferenceNumber: rewardPromotionUrn,
          loyaltyProgrammeTierUniqueReferenceNumber: rewardTierUrn,
          isPartner: reward.isPartner ?? false,
          partnerProgrammeUniqueReferenceNumber:
            reward.partnerProgrammeUniqueReferenceNumber ?? "",
          partnerCustomerUniqueReferenceNumber:
            reward.partnerCustomerUniqueReferenceNumber ?? "",
          partnerMerchantUniqueReferenceNumber:
            reward.partnerMerchantUniqueReferenceNumber ?? "",
        },
      };
    }

    if (pointMerchant && redeemedPoints > 0) {
      let loyaltyMemberUniqueReferenceNumber =
        pointMerchant.loyaltyProgrammeMemberUniqueReferenceNumber;

      if (!loyaltyMemberUniqueReferenceNumber) {
        try {
          const members = await getAllEnrolledLoyalty({
            CustomerUniqueReferenceNumber:
              storeProfile.customerUniqueReferenceNumber,
            ConsumerUniqueReferenceNumber: user.consumerUniqueReferenceNumber,
            State: "Enrolled",
          });

          const matchedMember = (members.data || []).find((member: ILoyaltyMember) => {
            const merchantMatch =
              !member?.merchantUniqueReferenceNumber ||
              member.merchantUniqueReferenceNumber ===
              pointMerchant.merchantUniqueReferenceNumber;
            const programmeMatch =
              member?.loyaltyProgrammeUniqueReferenceNumber ===
              pointMerchant.id?.loyaltyProgrammeUniqueReferenceNumber;

            return merchantMatch && programmeMatch;
          });

          loyaltyMemberUniqueReferenceNumber =
            matchedMember?.uniqueReferenceNumber ||
            loyaltyMemberUniqueReferenceNumber;
        } catch (error) {
          console.log(
            "Unable to resolve loyalty member URN from members API",
            error,
          );
        }
      }

      payload = {
        ...payload,
        pointsRedemption: {
          loyaltyProgrammeTierUniqueReferenceNumber:
            pointMerchant?.loyaltyProgrammeTierUniqueReferenceNumber,
          loyaltyProgrammeUniqueReferenceNumber:
            pointMerchant.id.loyaltyProgrammeUniqueReferenceNumber,
          loyaltyMemberUniqueReferenceNumber:
            loyaltyMemberUniqueReferenceNumber as string,
          customerUniqueReferenceNumber:
            pointMerchant.customerUniqueReferenceNumber,
          points: redeemedPoints,
        },
      };
    }

    if (freeShippingReward) {
      const shippingDiscount = Math.min(
        shippingRate?.charge ?? 0,
        Number((freeShippingReward.rewardEffectValueLimit ?? 0) / 100),
      );
      payload = {
        ...payload,
        shippingRewardRedemption: {
          uniqueReferenceNumber: freeShippingReward.uniqueReferenceNumber,
          discountAmountApplied: convertFromNairaToKobo(
            parseFloat(shippingDiscount.toFixed(2)),
          ),
          customerUniqueReferenceNumber:
            freeShippingReward.customerUniqueReferenceNumber ?? "",
          promotionUniqueReferenceNumber:
            freeShippingReward.promotionUniqueReferenceNumber ?? "",
          loyaltyProgrammeTierUniqueReferenceNumber:
            freeShippingReward.loyaltyProgrammeTierUniqueReferenceNumber ?? "",
          isPartner: freeShippingReward.isPartner ?? false,
          partnerProgrammeUniqueReferenceNumber:
            freeShippingReward.partnerProgrammeUniqueReferenceNumber ?? "",
          partnerCustomerUniqueReferenceNumber:
            freeShippingReward.partnerCustomerUniqueReferenceNumber ?? "",
          partnerMerchantUniqueReferenceNumber:
            freeShippingReward.partnerMerchantUniqueReferenceNumber ?? "",
        },
      };
    }

    console.log("Checkout Payload", payload);

    onCheckoutWalletSession.mutate(payload);
  };

  return (
    <PyButton
      click={handleCheckout}
      loading={onCheckoutWalletSession.isLoading}
      isDisabled={!canCheckout || isDisabled}
      extraClass="!w-full !text-white lg:!w-fit"
    >
      Pay{" "}
      <NumericFormat
        value={Math.max(0, amountToPay)}
        displayType={"text"}
        thousandSeparator={true}
        prefix={currency}
        className="text-white font-semibold leading-[140%] tracking-[-0.02em]"
      />
    </PyButton>
  );
};
